import {Injectable,} from '@angular/core';
import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators,} from '@angular/forms';
import {CheckValByrefArgVO,} from '@business-common/business-common-vo/check-val-byref-arg-vo';
import {MessageLevel, MessageVO,} from '@models/index';
import {
  Logger,
  MessageProvider,
  StringUtil,
  MessageStackService,
  InputCheck,
  DateUtil,
  SystemConstant,
} from '@services/index';
import * as Encoding from 'encoding-japanese';
import * as moment from 'moment';

/**
 * Validationユーティリティー
 */
@Injectable({
  providedIn: 'root',
})
export class ValidationService {

  readonly constantOfSuji = '[0-9]';
  readonly patternOfAlphabet = '^[a-zA-Z]*$';
  readonly patternOfZenAlphabetSpace = '^[　Ａ-Ｚａ-ｚ]*$';
  readonly patternOfHankaku = '^[0-9a-zA-Z]*$';
  readonly patternOfHankakuKana = '^[\uFF61-\uFF9F]*$';
  readonly patternOfHankakuKatakana = '^[ｧ-ﾝﾞﾟ\\-]*$';
  readonly patternOfZenkakuHiragana = '^[ぁ-んー]*$';
  readonly patternOfZenkakuKatakana = '^[ァ-ンヴー]*$';
  readonly patternOfAscii = '^[\x20-\x7E]*$';
  readonly patternOfNumeric = '^[0-9]*$';
  readonly patternOfUserId = '^[0-9A-Z-]*$';
  readonly patternOfPasswordIgnore = '"';
  readonly patternOfSujiWithPeriod = '^[0-9.]*$';
  readonly patternOfHankakuWithKigo = '^[0-9a-zA-Z!$&*.=^`|~#%+\\/?_{}-]*$';
  readonly patternOfHankakuWithOutKigo = '^[\uFF61-\uFF9F a-zA-Z0-9-]*$';
  readonly patternOfZenkakuKigo = '！＃＄％＆（）＊＋、。／・：；＜＞＝？＠［］￥｛｝＿「」’｜－ー～';
  readonly constantOfExtendJis = '﨎朗祥﨡﨑﨣福羽﨤﨧逸益靖飯都飼晴﨓凞精諸隆館﨔﨨﨩﨏塚礼﨟猪神蘒鶴';
  readonly patternOfExtendKigo = 'Ⅰ-Ⅹⅰ-ⅹΑ-Ωα-ω';
  readonly zenkakuKigoForShouhin = '！＃＄％＆（）＊＋、。．／・：；＜＞＝？＠［］￥｛｝＿「」’｜－ー～';
  readonly patternOfZenkakuWithKigo = '[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFF　Ａ-Ｚａ-ｚ０-９' + this.patternOfZenkakuKigo + '々' + this.constantOfExtendJis + ']';
  readonly patternOfZenkakuWithKigoExtKigo = '[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFF　Ａ-Ｚａ-ｚ０-９' + this.patternOfZenkakuKigo + '々' + this.constantOfExtendJis + this.patternOfExtendKigo + ']';
  readonly patternOfZenkakuWithKigoForShouhin = '^[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFF　Ａ-Ｚａ-ｚ０-９' + this.zenkakuKigoForShouhin + '々' + this.constantOfExtendJis + ']*$';
  readonly patternOfZenkakuAndKigoWithOutBlank = '^[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFFＡ-Ｚａ-ｚ０-９' + this.patternOfZenkakuKigo + '々' + ']*$';
  readonly constantOfKana = '[アイウエオ-ヂツ-モヤユヨ-ロワ-ンヴー]';
  readonly constantOfKanaWithKakko = '[アイウエオ-ヂツ-モヤユヨ-ロワ-ンヴー（）]';
  readonly patternOfKana = '^' + this.constantOfKana + '*$';
  readonly patternOfKanaSpace = '^[アイウエオ-ヂツ-モヤユヨ-ロワ-ンヴー　]*$';
  readonly patternOfKanaWithKakko = '^' + this.constantOfKanaWithKakko + '*$';
  readonly patternOfMinusNumber = '^(0|-?[1-9]([0-9])*)$';
  readonly patternOfMenuNo = '^[0-9][0-9]\\.[0-9][0-9]\\.[0-9][0-9]$';
  readonly patternOfMailAddress = '^[a-zA-Z0-9!$&*.=^`|~#%\'+\\/?_{}-]+@([a-zA-Z0-9_-]+\\.)+[a-zA-Z]{2,4}$';
  readonly patternOfKeiyakuUketukeNumber = '^[A-Z][A-L][0-9]{8,9}$';
  readonly patternOfHikiateKozaMeigiKana = '^[Ａ-Ｚ０-９アイウエオ-ヂツ-モヤユヨ-ロワ-ンヴー（）＋’，．／：？]*$';
  readonly consKozaMeigiKana = '[Ａ-Ｚ０-９アイウエオ-ヂツ-モヤユヨ-ロワ-ンヴー（）－＋’，．／：？]';
  readonly patternOfExistInDnp = '^[^丨]{1,4000}$';
  readonly patternOfExistInDnp2 = '^[^丨∑∟∮⊿〟㊤㊥㊦㊧㊨㍑㍻㍼㍽㍾￢]{1,4000}$';
  readonly patternOfKozaMeigi = '[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFF　Ａ-Ｚａ-ｚ０-９ー々（）－＋’，．／：？' + this.constantOfExtendJis + ']';
  readonly patternOfZenkakuWithOutBlank = '[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFFＡ-Ｚａ-ｚ０-９－ー々' + this.constantOfExtendJis + ']';
  readonly patternOfZenkaku = '[\u30A0-\u30FF\u3040-\u309F\u4E00-\u9FFF　Ａ-Ｚａ-ｚ０-９－ー々' + this.constantOfExtendJis + ']';

  constructor(
    private messageProvider: MessageProvider,
    private logger: Logger,
    private stringUtil: StringUtil,
    private inputCheck: InputCheck,
    private dateUtil: DateUtil
  ) {}

  /**
   * [一般開発者利用不可]
   * FormControlを受取り、Validationの状態がInvalidの場合に、
   * Validationエラーに応じてメッセージを返す。
   * HTMLからの利用例:
   * <mat-error *ngIf="form.get('email')?.invalid">{{validationUtil.getErrorMessage(form.get('email'))}}</mat-error>
   * @param control FormControlオブジェクト
   * @param customMessage バリデーションエラー毎に定義したいカスタムメッセージ(Key:バリデータの種別, Value:MessageVO型)
   * customMessage例: {'required': {messageId: 'CSS01999M030E'}, 'minlength': {messageId: 'CSS01999M031E', messageParams: [1]}}
   */
  getErrorMessage(control: AbstractControl | null | undefined,
                  customMessage: {[key:string]: MessageVO} = {}): string | null {
    let message: string | null = null;
    if(control != null) {
      if (control.hasError('serverSideInputValidation')) {
        message = control?.getError('serverSideInputValidation');
      } else if (control.hasError('required')) {
        message = this.messageProvider.getMessageString(
          customMessage['required'] ?? {messageId: 'CSS02999M015E',}
        );
      } else if (control.hasError('min')) {
        message = this.messageProvider.getMessageString(
          customMessage['min'] ?? {messageId: 'CSS02999M016E', messageParams: [control?.errors?.min?.min,],}
        );
      } else if (control.hasError('max')) {
        message = this.messageProvider.getMessageString(
          customMessage['max'] ?? {messageId: 'CSS02999M017E', messageParams: [control?.errors?.max?.max,],}
        );
      } else if (control.hasError('mailAddress')) {
        message = this.messageProvider.getMessageString(
          customMessage['mailAddress'] ?? {messageId: 'CSS02999M018E',}
        );
      } else if (control.hasError('minlength')) {
        message = this.messageProvider.getMessageString(
          customMessage['minlength'] ?? {messageId: 'CSS02999M019E', messageParams: [control?.errors?.minlength?.requiredLength,],}
        );
      } else if (control.hasError('maxlength')) {
        message = this.messageProvider.getMessageString(
          customMessage['maxlength'] ?? {messageId: 'CSS02999M020E', messageParams: [control?.errors?.maxlength?.requiredLength,],}
        );
      } else if (control.hasError('pattern')) {
        message = this.messageProvider.getMessageString(
          customMessage['pattern'] ?? {messageId: 'CSS02999M021E',}
        );
      } else if (control.hasError('alphabet')) {
        message = this.messageProvider.getMessageString(
          customMessage['alphabet'] ?? {messageId: 'CSS02999M022E',}
        );
      } else if (control.hasError('numeric')) {
        message = this.messageProvider.getMessageString(
          customMessage['numeric'] ?? {messageId: 'CSS02999M055E',}
        );
      } else if (control.hasError('hankaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankaku'] ?? {messageId: 'CSS02999M024E',}
        );
      } else if (control.hasError('zenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkaku'] ?? {messageId: 'CSS02999M025E',}
        );
      } else if (control.hasError('hankakuKatakana')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuKatakana'] ?? {messageId: 'CSS02999M026E',}
        );
      } else if (control.hasError('zenkakuHiragana')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuHiragana'] ?? {messageId: 'CSS02999M027E',}
        );
      } else if (control.hasError('zenkakuKatakana')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuKatakana'] ?? {messageId: 'CSS02999M028E',}
        );
      } else if (control.hasError('existInSjis')) {
        message = this.messageProvider.getMessageString(
          customMessage['existInSjis'] ?? {messageId: 'CSS02999M040E',}
        );
      } else if (control.hasError('existInSjisZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['existInSjisZenkaku'] ?? {messageId: 'CSS02999M040E',}
        );
      } else if (control.hasError('existInDnp')) {
        message = this.messageProvider.getMessageString(
          customMessage['existInDnp'] ?? {messageId: 'CSS02999M040E',}
        );
      } else if (control.hasError('existInDnp2')) {
        message = this.messageProvider.getMessageString(
          customMessage['existInDnp2'] ?? {messageId: 'CSS02999M040E',}
        );
      } else if (control.hasError('zipCode')) {
        message = this.messageProvider.getMessageString(
          customMessage['zipCode'] ?? {messageId: 'CSS02999M041E',}
        );
      } else if (control.hasError('zipCode1')) {
        message = this.messageProvider.getMessageString(
          customMessage['zipCode1'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('zipCode2')) {
        message = this.messageProvider.getMessageString(
          customMessage['zipCode2'] ?? {messageId: 'CSS02999M043E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('telNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['telNo'] ?? {messageId: 'CSS02999M044E',}
        );
      } else if (control.hasError('telNo1')) {
        message = this.messageProvider.getMessageString(
          customMessage['telNo1'] ?? {messageId: 'CSS02999M043E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('telNo2')) {
        message = this.messageProvider.getMessageString(
          customMessage['telNo2'] ?? {messageId: 'CSS02999M043E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('telNo3')) {
        message = this.messageProvider.getMessageString(
          customMessage['telNo3'] ?? {messageId: 'CSS02999M043E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('keitaiTelNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['keitaiTelNo'] ?? {messageId: 'CSS02999M045E',}
        );
      } else if (control.hasError('keitaiTelNo1')) {
        message = this.messageProvider.getMessageString(
          customMessage['keitaiTelNo1'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('keitaiTelNo2')) {
        message = this.messageProvider.getMessageString(
          customMessage['keitaiTelNo2'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('keitaiTelNo3')) {
        message = this.messageProvider.getMessageString(
          customMessage['keitaiTelNo3'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('seiKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['seiKana'] ?? {messageId: 'CSS02999M046E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('seiZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['seiZenkaku'] ?? {messageId: 'CSS02999M047E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('meiKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['meiKana'] ?? {messageId: 'CSS02999M046E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('meiZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['meiZenkaku'] ?? {messageId: 'CSS02999M047E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('companyNameKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['companyNameKana'] ?? {messageId: 'CSS02999M048E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('companyNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['companyNameZenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('sitenNameKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['sitenNameKana'] ?? {messageId: 'CSS02999M048E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('sitenNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['sitenNameZenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('bushoNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['bushoNameZenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('prefNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['prefNameZenkaku'] ?? {messageId: 'CSS02999M047E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('cityNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['cityNameZenkaku'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName1Zenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName1Zenkaku'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName2Zenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName2Zenkaku'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName3Zenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName3Zenkaku'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName4Zenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName4Zenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('atena')) {
        message = this.messageProvider.getMessageString(
          customMessage['atena'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('tuchoKigo')) {
        message = this.messageProvider.getMessageString(
          customMessage['tuchoKigo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('tuchoNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['tuchoNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('kinyuKikanNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['kinyuKikanNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('kinyuKikanSitenNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['kinyuKikanSitenNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('kozaNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('kozaNameKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNameKana'] ?? {messageId: 'CSS02999M051E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('kozaNameKanaNotSpace')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNameKanaNotSpace'] ?? {messageId: 'CSS02999M052E',}
        );
      } else if (control.hasError('kozaNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNameZenkaku'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('bldgNameKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['bldgNameKana'] ?? {messageId: 'CSS02999M046E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('bldgNameZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['bldgNameZenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('roomNoZenkaku')) {
        message = this.messageProvider.getMessageString(
          customMessage['roomNoZenkaku'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('userId')) {
        message = this.messageProvider.getMessageString(
          customMessage['userId'] ?? {messageId: 'CSS02999M053E',}
        );
      } else if (control.hasError('passWord')) {
        message = this.messageProvider.getMessageString(
          customMessage['passWord'] ?? {messageId: 'CSS02999M054E',}
        );
      } else if (control.hasError('kissCode1')) {
        message = this.messageProvider.getMessageString(
          customMessage['kissCode1'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('kissCode2')) {
        message = this.messageProvider.getMessageString(
          customMessage['kissCode2'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('onlyNumberComma')) {
        message = this.messageProvider.getMessageString(
          customMessage['onlyNumberComma'] ?? {messageId: 'CSS02999M021E',}
        );
      } else if (control.hasError('numberAndPeriod')) {
        message = this.messageProvider.getMessageString(
          customMessage['numberAndPeriod'] ?? {messageId: 'CSS02999M056E',}
        );
      } else if (control.hasError('numberAndPeriodWithSize')) {
        message = this.messageProvider.getMessageString(
          customMessage['numberAndPeriodWithSize'] ?? {
            messageId: 'CSS02999M007E',
            messageParams: [control?.errors?.inSize, control?.errors?.decSize,],
          }
        );
      } else if (control.hasError('hankakuAndKigo')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuAndKigo'] ?? {messageId: 'CSS02999M057E',}
        );
      } else if (control.hasError('zenkakuWithoutBlank')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuWithoutBlank'] ?? {messageId: 'CSS02999M058E',}
        );
      } else if (control.hasError('zenkakuAndKigo')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuAndKigo'] ?? {messageId: 'CSS02999M060E',}
        );
      } else if (control.hasError('zenkakuAndKigoForShouhin')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuAndKigoForShouhin'] ?? {messageId: 'CSS02999M060E',}
        );
      } else if (control.hasError('zenkakuAndKigoWithoutBlank')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuAndKigoWithoutBlank'] ?? {messageId: 'CSS02999M061E',}
        );
      } else if (control.hasError('kana')) {
        message = this.messageProvider.getMessageString(customMessage['kana'] ?? {messageId: 'CSS02999M002E',});
      } else if (control.hasError('allKana')) {
        message = this.messageProvider.getMessageString(
          customMessage['allKana'] ?? {messageId: 'CSS02999M062E',}
        );
      } else if (control.hasError('minusNumber')) {
        message = this.messageProvider.getMessageString(
          customMessage['minusNumber'] ?? {messageId: 'CSS02999M063E',}
        );
      } else if (control.hasError('kanaSpace')) {
        message = this.messageProvider.getMessageString(
          customMessage['kanaSpace'] ?? {messageId: 'CSS02999M064E',}
        );
      } else if (control.hasError('onlyOneSpace')) {
        message = this.messageProvider.getMessageString(
          customMessage['onlyOneSpace'] ?? {messageId: 'CSS02999M065E',}
        );
      } else if (control.hasError('menuNO')) {
        message = this.messageProvider.getMessageString(
          customMessage['menuNO'] ?? {messageId: 'CSS02999M066E',}
        );
      } else if (control.hasError('shouninNumber')) {
        message = this.messageProvider.getMessageString(
          customMessage['shouninNumber'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('shouninNumberLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['shouninNumberLen'] ?? {messageId: 'CSS02999M106E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('keiyakuUketukeNumber')) {
        message = this.messageProvider.getMessageString(
          customMessage['keiyakuUketukeNumber'] ?? {messageId: 'CSS02999M067E',}
        );
      } else if (control.hasError('tradeNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['tradeNo'] ?? {messageId: 'CSS02999M068E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('untenMenkyoNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['untenMenkyoNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('mPKeiyakuNo')) {
        message = this.messageProvider.getMessageString(
          customMessage['mPKeiyakuNo'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('area')) {
        message = this.messageProvider.getMessageString(
          customMessage['area'] ?? {messageId: 'CSS02999M069E',}
        );
      } else if (control.hasError('rate')) {
        message = this.messageProvider.getMessageString(
          customMessage['rate'] ?? {messageId: 'CSS02999M070E',}
        );
      } else if (control.hasError('gaisanritsu')) {
        message = this.messageProvider.getMessageString(
          customMessage['gaisanritsu'] ?? {messageId: 'CSS02999M071E',}
        );
      } else if (control.hasError('dateYYYYMMDD')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateYYYYMMDD'] ?? {messageId: 'CSS02999M072E',}
        );
      } else if (control.hasError('dateYYYYMM')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateYYYYMM'] ?? {messageId: 'CSS02999M073E',}
        );
      } else if (control.hasError('dateYYYYMMDDhhmmss')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateYYYYMMDDhhmmss'] ?? {messageId: 'CSS02999M074E',}
        );
      // ★
      } else if (control.hasError('dateYYYY')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateYYYY'] ?? {messageId: 'CSS02999M108E',}
        );
      } else if (control.hasError('dateMM')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateMM'] ?? {messageId: 'CSS02999M109E',}
        );
      } else if (control.hasError('dateDD')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateDD'] ?? {messageId: 'CSS02999M110E',}
        );
      // ★
      } else if (control.hasError('alphabetSpace')) {
        message = this.messageProvider.getMessageString(
          customMessage['alphabetSpace'] ?? {messageId: 'CSS02999M075E',}
        );
      } else if (control.hasError('dateFromTo')) {
        message = this.messageProvider.getMessageString(
          customMessage['dateFromTo'] ?? {messageId: 'CSS02999M076E',}
        );
      } else if (control.hasError('hankakuWithoutKigo')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuWithoutKigo'] ?? {messageId: 'CSS02999M077E',}
        );
      } else if (control.hasError('matDatepickerParse')) {
        message = this.messageProvider.getMessageString(
          customMessage['matDatepickerParse'] ?? {messageId: 'CSS02999M072E',}
        );
      } else if (control.hasError('lengthCheck')) {
        message = this.messageProvider.getMessageString(
          customMessage['lengthCheck'] ?? {messageId: 'CSS02999M082E',}
        );
      } else if (control.hasError('onlyNumberLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['onlyNumberLen'] ?? {messageId: 'CSS02999M083E',}
        );
      } else if (control.hasError('minusNumberLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['minusNumberLen'] ?? {messageId: 'CSS02999M084E',}
        );
      } else if (control.hasError('numberAndPeriodLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['numberAndPeriodLen'] ?? {messageId: 'CSS02999M085E',}
        );
      } else if (control.hasError('hankakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuLen'] ?? {messageId: 'CSS02999M086E',}
        );
      } else if (control.hasError('zenkakuWithoutBlankLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuWithoutBlankLen'] ?? {messageId: 'CSS02999M087E',}
        );
      } else if (control.hasError('zenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuLen'] ?? {messageId: 'CSS02999M088E',}
        );
      } else if (control.hasError('zenkakuAndKigoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuAndKigoLen'] ?? {messageId: 'CSS02999M089E',}
        );
      } else if (control.hasError('zenkakuAndKigoForShouhinLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['zenkakuAndKigoForShouhinLen'] ?? {messageId: 'CSS02999M099E',}
        );
      } else if (control.hasError('hankakuKanaLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuKanaLen'] ?? {messageId: 'CSS02999M090E',}
        );
      } else if (control.hasError('kanaLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['kanaLen'] ?? {messageId: 'CSS02999M091E',}
        );
      } else if (control.hasError('allKanaLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['allKanaLen'] ?? {messageId: 'CSS02999M092E',}
        );
      } else if (control.hasError('kanaSpaceLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['kanaSpaceLen'] ?? {messageId: 'CSS02999M093E',}
        );
      } else if (control.hasError('alphabetSpaceLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['alphabetSpaceLen'] ?? {messageId: 'CSS02999M094E',}
        );
      } else if (control.hasError('extInSjLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['extInSjLen'] ?? {messageId: 'CSS02999M040E',}
        );
      } else if (control.hasError('hankakuWithoutKigoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['hankakuWithoutKigoLen'] ?? {messageId: 'CSS02999M100E',}
        );
      } else if (control.hasError('companyNameZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['companyNameZenkakuLen'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('companyNameKanaLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['companyNameKanaLen'] ?? {messageId: 'CSS02999M048E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('bldgNameZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['bldgNameZenkakuLen'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('roomNameZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['roomNameZenkakuLen'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('roomNoZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['roomNoZenkakuLen'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName3ZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName3ZenkakuLen'] ?? {messageId: 'CSS02999M050E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('addName4ZenkakuLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['addName4ZenkakuLen'] ?? {messageId: 'CSS02999M049E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('zipCodeLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['zipCodeLen'] ?? {messageId: 'CSS02999M041E',}
        );
      } else if (control.hasError('telNoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['telNoLen'] ?? {messageId: 'CSS02999M044E',}
        );
      } else if (control.hasError('keitaiTelNoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['keitaiTelNoLen'] ?? {messageId: 'CSS02999M045E',}
        );
      } else if (control.hasError('kozaNameKanaLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNameKanaLen'] ?? {messageId: 'CSS02999M096E', messageParams: [control?.errors?.minLength, control?.errors?.maxLength,],}
        );
      } else if (control.hasError('kozaNameKanaNotSpaceLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['kozaNameKanaNotSpaceLen'] ?? {messageId: 'CSS02999M097E',}
        );
      } else if (control.hasError('untenMenkyoNoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['untenMenkyoNoLen'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('mPKeiyakuNoLen')) {
        message = this.messageProvider.getMessageString(
          customMessage['mPKeiyakuNoLen'] ?? {messageId: 'CSS02999M042E', messageParams: [control?.errors?.requiredLength,],}
        );
      } else if (control.hasError('maxlengthWithoutComma')) {
        message = this.messageProvider.getMessageString(
          customMessage['maxlengthWithoutComma'] ?? {messageId: 'CSS02999M020E', messageParams: [control?.errors?.maxLength,],}
        );
      } else if (control.hasError('informationIsNumeric')) {
        message = this.messageProvider.getMessageString(
          customMessage['informationIsNumeric'] ?? {messageId: 'CSS02999M101E',}
        );
      } else if (control.hasError('numberFromTo')) {
        message = this.messageProvider.getMessageString(
          customMessage['numberFromTo'] ?? {messageId: 'CSS02999M102E',}
        );
      } else if (control.hasError('invalidLeapYear')) {
        message = this.messageProvider.getMessageString(
          customMessage['invalidLeapYear'] ?? {messageId: 'CSS02999M105E',}
        );
      } else if (control.hasError('day27Only')) {
        message = this.messageProvider.getMessageString(
          customMessage['day27Only'] ?? {messageId: 'CSS02999M107E',}
        );
      }
    }
    return message;
  }

  /*************************************************************************
   * 以降はFormで利用するカスタムバリデーター *
   ************************************************************************/

  /**
   * [一般開発者利用可]
   * アルファベットかどうかを判定するバリデータ
   */
  alphabetValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAlphabet(control.value) ? null : {alphabet: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 全角アルファベットと空白かどうかを判定するバリデータ
   */
  alphabetSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAlphabetSpace(control.value) ? null : {alphabetSpace: true,};
    };
  }

  /**
   * [一般開発者利用可]
   *  Shift_JISでの存在チェック
   */
  existInSjisValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.existInSjis(control.value) ? null : {existInSjis: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * Shift_JISでの存在チェック(全角のみ)
   */
  existInSjisZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.existInSjisZenkaku(control.value) ? null : {existInSjisZenkaku: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * DNP対応文字チェック
   */
  existInDnpValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.existInDnp(control.value) ? null : {existInDnp: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * DNP対応文字チェック２
   */
  existInDnp2Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.existInDnp2(control.value) ? null : {existInDnp2: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * ZipCode
   *
   * @param {string} zipCode1 郵便番号1オブジェクト
   * @param {string} zipCode2 郵便番号2オブジェクト
   *
   */
  zipCodeValidator(zipCode1: string, zipCode2: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const dataControl1 = group?.get(zipCode1);
      const dataControl2 = group?.get(zipCode2);
      dataControl1?.setErrors(null);
      dataControl2?.setErrors(null);
      if (this.isZipCode(dataControl1?.value, dataControl2?.value)) {
        return null;
      } else {
        dataControl1?.setErrors({zipCode: true,});
        dataControl1?.markAsDirty();
        dataControl2?.setErrors({zipCode: true,});
        dataControl2?.markAsDirty();
        return {zipCode: true,};
      }
    };
  }

  /**
   * [一般開発者利用可]
   * ZipCode 1
   */
  zipCode1Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZipCode1(control.value) ? null : {zipCode1: true, requiredLength: 3,};
    };
  }

  /**
   * [一般開発者利用可]
   * ZipCode 2
   */
  zipCode2Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZipCode2(control.value) ? null : {zipCode2: true, minLength: 1, maxLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * 電話番号
   *
   * @param {string} telNo1 電話番号1オブジェクト
   * @param {string} telNo2 電話番号2オブジェクト
   * @param {string} telNo3 電話番号3オブジェクト
   *
   */
  telNoValidator(telNo1: string, telNo2: string, telNo3: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const ctrTelNo1 = group?.get(telNo1);
      const ctrTelNo2 = group?.get(telNo2);
      const ctrTelNo3 = group?.get(telNo3);
      ctrTelNo1?.setErrors(null);
      ctrTelNo2?.setErrors(null);
      ctrTelNo3?.setErrors(null);

      //値が空白のため入力チェック処理を行わない
      if (ctrTelNo1?.value === SystemConstant.EMPTY && ctrTelNo2?.value === SystemConstant.EMPTY && ctrTelNo3?.value === SystemConstant.EMPTY) {
        if (ctrTelNo1.hasValidator(Validators.required)) {
          ctrTelNo1?.setErrors({required: true,});
          ctrTelNo1?.markAsDirty();
        }
        if (ctrTelNo2.hasValidator(Validators.required)) {
          ctrTelNo2?.setErrors({required: true,});
          ctrTelNo2?.markAsDirty();
        }
        if (ctrTelNo3.hasValidator(Validators.required)) {
          ctrTelNo3?.setErrors({required: true,});
          ctrTelNo3?.markAsDirty();
        }

        return null;
      }

      if (this.isTelNo(ctrTelNo1?.value, ctrTelNo2?.value, ctrTelNo3?.value)) {
        return null;
      } else {
        ctrTelNo1?.setErrors({telNo: true,});
        ctrTelNo1?.markAsDirty();

        ctrTelNo2?.setErrors({telNo: true,});
        ctrTelNo2?.markAsDirty();

        ctrTelNo3?.setErrors({telNo: true,});
        ctrTelNo3?.markAsDirty();
        return {telNo: true,};
      }
    };
  }

  /**
   * [一般開発者利用可]
   * 電話番号No1
   */
  telNo1Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isTelNo1(control.value) ? null : {telNo1: true, minLength: 2, maxLength: 5,};
    };
  }

  /**
   * [一般開発者利用可]
   * 電話番号No2
   */
  telNo2Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isTelNo2(control.value) ? null : {telNo2: true, minLength: 1, maxLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * 電話番号No3
   */
  telNo3Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isTelNo3(control.value) ? null : {telNo3: true, minLength: 4, maxLength: 6,};
    };
  }

  /**
   * [一般開発者利用可]
   * 携帯電話番号
   *
   * @param {string} keitaiTelNo1 携帯電話番号1オブジェクト
   * @param {string} keitaiTelNo2 携帯電話番号2オブジェクト
   * @param {string} keitaiTelNo3 携帯電話番号3オブジェクト
   */
  keitaiTelNoValidator(keitaiTelNo1: string, keitaiTelNo2: string, keitaiTelNo3: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const ctrKeitaiTelNo1 = group?.get(keitaiTelNo1);
      const ctrKeitaiTelNo2 = group?.get(keitaiTelNo2);
      const ctrKeitaiTelNo3 = group?.get(keitaiTelNo3);
      ctrKeitaiTelNo1?.setErrors(null);
      ctrKeitaiTelNo2?.setErrors(null);
      ctrKeitaiTelNo3?.setErrors(null);

      //値が空白のため入力チェック処理を行わない
      if (ctrKeitaiTelNo1?.value === SystemConstant.EMPTY && ctrKeitaiTelNo2?.value === SystemConstant.EMPTY && ctrKeitaiTelNo3?.value === SystemConstant.EMPTY) {
        if (ctrKeitaiTelNo1.hasValidator(Validators.required)) {
          ctrKeitaiTelNo1?.setErrors({required: true,});
          ctrKeitaiTelNo1?.markAsDirty();
        }
        if (ctrKeitaiTelNo2.hasValidator(Validators.required)) {
          ctrKeitaiTelNo2?.setErrors({required: true,});
          ctrKeitaiTelNo2?.markAsDirty();
        }
        if (ctrKeitaiTelNo3.hasValidator(Validators.required)) {
          ctrKeitaiTelNo3?.setErrors({required: true,});
          ctrKeitaiTelNo3?.markAsDirty();
        }

        return null;
      }

      if (this.isKeitaiTelNo(ctrKeitaiTelNo1?.value, ctrKeitaiTelNo2?.value, ctrKeitaiTelNo3?.value)) {
        return null;
      } else {
        ctrKeitaiTelNo1?.setErrors({keitaiTelNo: true,});
        ctrKeitaiTelNo1?.markAsDirty();

        ctrKeitaiTelNo2?.setErrors({keitaiTelNo: true,});
        ctrKeitaiTelNo2?.markAsDirty();

        ctrKeitaiTelNo3?.setErrors({keitaiTelNo: true,});
        ctrKeitaiTelNo3?.markAsDirty();
        return {keitaiTelNo: true,};
      }
    }
  }

  /**
   * [一般開発者利用可]
   * Keitai電話番号NO1
   */
  keitaiTelNo1Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKeitaiTelNo1(control.value) ? null : {keitaiTelNo1: true, requiredLength: 3,};
    };
  }

  /**
   * [一般開発者利用可]
   * Keitai電話番号NO2
   */
  keitaiTelNo2Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKeitaiTelNo2(control.value) ? null : {keitaiTelNo2: true, requiredLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * Keitai電話番号NO3
   */
  keitaiTelNo3Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKeitaiTelNo3(control.value) ? null : {keitaiTelNo3: true, requiredLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * 姓氏 (カナ)
   */
  seiKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isSeiKana(control.value) ? null : {seiKana: true, minLength: 1, maxLength: 20,};
    };
  }

  /**
   * [一般開発者利用可]
   * 姓名 (全角)
   */
  seiZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isSeiZenkaku(control.value) ? null : {seiZenkaku: true, minLength: 1, maxLength: 10,};
    };
  }

  /**
   * [一般開発者利用可]
   * 名字 (カナ)
   */
  meiKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isMeiKana(control.value) ? null : {meiKana: true, minLength: 1, maxLength: 20,};
    };
  }

  /**
   * [一般開発者利用可]
   * 名字 (全角)
   */
  meiZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isMeiZenkaku(control.value) ? null : {meiZenkaku: true, minLength: 1, maxLength: 10,};
    };
  }

  /**
   * [一般開発者利用可]
   * 会社名(カナ)
   */
  companyNameKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isCompanyNameKana(control.value) ? null : {companyNameKana: true, minLength: 1, maxLength: 100,};
    };
  }

  /**
   * [一般開発者利用可]
   * 会社名(全角)
   */
  companyNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isCompanyNameZenkaku(control.value) ? null : {companyNameZenkaku: true, minLength: 1, maxLength: 50,};
    };
  }

  /**
   * [一般開発者利用可]
   * 支店名(カナ)るバリデータ
   */
  sitenNameKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isSitenNameKana(control.value) ? null : {sitenNameKana: true, minLength: 1, maxLength: 100,};
    };
  }

  /**
   * [一般開発者利用可]
   * 支店名(全角)るバリデータ
   */
  sitenNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isSitenNameZenkaku(control.value) ? null : {sitenNameZenkaku: true, minLength: 1, maxLength: 50,};
    };
  }

  /**
   * [一般開発者利用可]
   * 部署名(全角)をバリデートする。
   */
  bushoNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isBushoNameZenkaku(control.value) ? null : {bushoNameZenkaku: true, minLength: 1, maxLength: 50,};
    };
  }

  /**
   * [一般開発者利用可]
   * 都道府県をバリデートする。
   */
  prefNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isPrefNameZenkaku(control.value) ? null : {prefNameZenkaku: true, minLength: 3, maxLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * 市名(全角)をバリデートする。
   */
  cityNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isCityNameZenkaku(control.value) ? null : {cityNameZenkaku: true , minLength: 1, maxLength: 12,};
    };
  }

  /**
   * [一般開発者利用可]
   * 市名1(全角)をバリデートする。
   */
  addName1ZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAddName1Zenkaku(control.value) ? null : {addName1Zenkaku: true , minLength: 1, maxLength: 18,};
    };
  }

  /**
   * [一般開発者利用可]
   * 市名2(全角)をバリデートする。
   */
  addName2ZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAddName2Zenkaku(control.value) ? null : {addName2Zenkaku: true , minLength: 1, maxLength: 12,};
    };
  }

  /**
   * [一般開発者利用可]
   * 市名3(全角)をバリデートする。
   */
  addName3ZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAddName3Zenkaku(control.value) ? null : {addName3Zenkaku: true , minLength: 1, maxLength: 10,};
    };
  }

  /**
   * [一般開発者利用可]
   * 市名4(全角)をバリデートする。
   */
  addName4ZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAddName4Zenkaku(control.value) ? null : {addName4Zenkaku: true , minLength: 1, maxLength: 50,};
    };
  }

  /**
   * [一般開発者利用可]
   * 各種書類送付先をバリデートする。
   */
  atenaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAtena(control.value) ? null : {atena: true , minLength: 1, maxLength: 40,};
    };
  }

  /**
   * [一般開発者利用可]
   * 通帳記号をバリデートする。
   */
  tuchoKigoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isTuchoKigo(control.value) ? null : {tuchoKigo: true, requiredLength: 5,};
    };
  }

  /**
   * [一般開発者利用可]
   * 通帳Noをバリデートする。
   */
  tuchoNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isTuchoNo(control.value) ? null : {tuchoNo: true, requiredLength: 8,};
    };
  }

  /**
   * [一般開発者利用可]
   * 金融機関コードをバリデートする。
   */
  kinyuKikanNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKinyuKikanNo(control.value) ? null : {kinyuKikanNo: true, requiredLength: 4,};
    };
  }

  /**
   * [一般開発者利用可]
   * 金融機関支店番号をバリデートする。
   */
  kinyuKikanSitenNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKinyuKikanSitenNo(control.value) ? null : {kinyuKikanSitenNo: true, requiredLength: 3,};
    };
  }

  /**
   * [一般開発者利用可]
   * 口座番号をバリデートする。
   */
  kozaNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKozaNo(control.value) ? null : {kozaNo: true, requiredLength: 7,};
    };
  }

  /**
   * [一般開発者利用可]
   * 口座名義（カナ）をバリデートする。
   */
  kozaNameKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isKozaNameKana(control.value) ? null : {kozaNameKana: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 口座名義（カナ）（引当用振込人名カナ用）をバリデートする。
   */
  kozaNameKanaNotSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKozaNameKanaNotSpace(control.value) ? null : {kozaNameKanaNotSpace: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 口座名義(全角)をバリデートする。
   */
  kozaNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKozaNameZenkaku(control.value) ? null : {kozaNameZenkaku: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 物件名(カナ)をバリデートする。
   */
  bldgNameKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isBldgNameKana(control.value) ? null : {bldgNameKana: true, minLength: 1, maxLength: 100,};
    }
  }

  /**
   * [一般開発者利用可]
   * 物件名(全角)をバリデートする。
   */
  bldgNameZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isBldgNameZenkaku(control.value) ? null : {bldgNameZenkaku: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 部屋番号(全角)をバリデートする。
   */
  roomNoZenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isRoomNoZenkaku(control.value) ? null : {roomNoZenkaku: true, minLength: 1, maxLength: 10,};
    }
  }

  /**
   * [一般開発者利用可]
   * ユーザーIDチェック(英字(大文字)、数字、「-(ハイフン)」のみ許可)をバリデートする。
   */
  userIdValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isUserId(control.value) ? null : {userId: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * パスワードチェック(「"(ダブルクォーテーション)」を除く)をバリデートする。
   */
  passWordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isPassWord(control.value) ? null : {passWord: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * KISSコード1をバリデートする。
   */
  kissCode1Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isKissCode1(control.value) ? null : {kissCode1: true, requiredLength: 6,};
    }
  }

  /**
   * [一般開発者利用可]
   * KISSコード2をバリデートする。
   */
  kissCode2Validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isKissCode2(control.value) ? null : {kissCode2: true, requiredLength: 3,};
    }
  }

  /**
   * [一般開発者利用可]
   * 数値どうかを判定するバリデータ
   */
  numericValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isNumeric(control.value) ? null : {numeric: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 数値どうかを判定するバリデータ(数値の範囲指定有り)
   */
  numericFromToValidator(from: number, to: number ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }

      // 数値で無い場合、エラー返却
      if (!this.isNumeric(control.value)) { return {numeric: true,}; }

      // from、toの範囲外の場合、エラー返却
      if (parseInt(control.value) < from) { return {numeric: true,}; }
      if (parseInt(control.value) > to  ) { return {numeric: true,}; }

      return null;
    };
  }

  /**
   * [一般開発者利用可]
   * 半角数字と小数点をバリデートする。
   */
  numberAndPeriodValidator(withoutCommas = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      let value = control.value;
      if (value != null && withoutCommas) {
        value = value.replace(/,/g, '');
      }
      return this.isNumberAndPeriod(value) ? null : {numberAndPeriod: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角文字と小数点(桁数チェック付)をバリデートする。
   */
  numberAndPeriodWithSizeValidator(insize: number , decsize: number, withoutCommas = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      let value = control.value;
      if (value != null && withoutCommas) {
        value = value.replace(/,/g, '');
      }
      return this.isNumberAndPeriodWithSize(value, insize, decsize) ? null : {numberAndPeriodWithSize: true, inSize: insize, decSize: decsize,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角数字(カンマ削除)をバリデートする。
   */
  onlyNumberCommaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isOnlyNumberComma(control.value) ? null : {onlyNumberComma: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角どうかを判定するバリデータ
   */
  hankakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isHankaku(control.value) ? null : {hankaku: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 半角カナ文字
   */
   hankakuKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isHankakuKana(control.value) ? null : {hankakuKana: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 半角カタカナかどうかを判定するバリデータ
   */
  hankakuKatakanaValidator(withoutSpace = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      let value = control.value;
      if (value != null && withoutSpace) {
        value = value.replace(/\s/g, '');
      }
      return this.isHankakuKatakana(value) ? null : {hankakuKatakana: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 半角文字と記号をバリデートする。
   */
  hankakuAndKigoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isHankakuAndKigo(control.value) ? null : {hankakuAndKigo: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 半角数字,半角カナ,半角アルファベット,半角スペース,半角ハイフンをバリデートする。
   */
  hankakuWithoutKigoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isHankakuWithoutKigo(control.value) ? null : {hankakuWithoutKigo: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 全角どうかを判定するバリデータ
   */
  zenkakuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkaku(control.value) ? null : {zenkaku: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 全角文字をバリデートする。
   */
  zenkakuWithoutBlankValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuWithoutBlank(control.value) ? null : {zenkakuWithoutBlank: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と記号と空白をバリデートする。
   */
  zenkakuAndKigoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuAndKigo(control.value) ? null : {zenkakuAndKigo: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と記号（""．""含む）と空白をバリデートする。
   */
  zenkakuAndKigoForShouhinValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuAndKigoForShouhin(control.value) ? null : {zenkakuAndKigoForShouhin: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と記号をバリデートする。
   */
  zenkakuAndKigoWithoutBlankValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuAndKigoWithoutBlank(control.value) ? null : {zenkakuAndKigoWithoutBlank: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナをバリデートする。
   */
  kanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKana(control.value) ? null : {kana: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナと（）をバリデートする。
   */
  allKanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isAllKana(control.value) ? null : {allKana: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 負の数を認める半角数字をバリデートする。
   */
  minusNumberValidator(withoutCommas = true): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      let value = control.value;
      if (value != null && withoutCommas) {
        value = value.replace(/,/g, '');
      }
      return this.isMinusNumber(value) ? null : {minusNumber: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナと空白をバリデートする。
   */
  kanaSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isKanaSpace(control.value) ? null : {kanaSpace: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角空白が１文字のみのチェックをバリデートする。
   */
  onlyOneSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isSpace(control.value) ? null : {onlyOneSpace: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * メニュー番号をバリデートする。
   */
  menuNOValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isMenuNO(control.value) ? null : {menuNO: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * メールアドレス。
   */
   mailAddressValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isMailAddress(control.value) ? null : {mailAddress: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 承認番号をバリデートする。
   */
  shouninNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isShouninNumber(control.value) ? null : {shouninNumber: true, requiredLength: 10,};
    }
  }

  /**
   * [一般開発者利用可]
   * 指定桁以上入力の際の承認番号をバリデートする。
   * @param {number} minLen 最小桁数
   */
  shouninNumberLenValidator(minLen: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isShouninNumberLen(control.value, minLen) ? null : {shouninNumberLen: true, requiredLength: minLen,};
    }
  }


  /**
   * [一般開発者利用可]
   * 契約受付番号をバリデートする。
   */
  keiyakuUketukeNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isKeiyakuUketukeNumber(control.value) ? null : {keiyakuUketukeNumber: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 取引番号をバリデートする。
   */
  tradeNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isTradeNo(control.value) ? null : {tradeNo: true, requiredLength: 6,};
    }
  }

  /**
   * [一般開発者利用可]
   * 運転免許証番号をバリデートする。
   */
  untenMenkyoNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isUntenMenkyoNo(control.value) ? null : {untenMenkyoNo: true, requiredLength: 12,};
    }
  }

  /**
   * [一般開発者利用可]
   * MP契約番号をバリデートする。
   */
  mPKeiyakuNoValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isMPKeiyakuNo(control.value) ? null : {mPKeiyakuNo: true, requiredLength: 12,};
    }
  }

  /**
   * [一般開発者利用可]
   * 占有面積をバリデートする。
   */
  areaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      return this.isArea(control.value) ? null : {area: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 代理店手数料率をバリデートする。
   */
  rateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
    if (this.getLength(control.value) === 0) {
      return null;
    }
    return this.isRate(control.value) ? null : {rate: true,};
  }
}

  /**
   * [一般開発者利用可]
   * 概算率チェックをバリデートする。
   */
  gaisanritsuValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isGaisanritsu(control.value) ? null : {gaisanritsu: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 日付型チェック(YYYYMMDD形式)をバリデートする。
   */
  dateYYYYMMDDValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      let value = control.value;
      if (typeof control.value.toISOString === 'function') {
        value = control.value.toISOString();
      }
      value = moment(value.toISOString).format('YYYYMMDD');
      return this.isDateYYYYMMDD(value) ? null : {dateYYYYMMDD: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 入力された日が27日であるかバリデートする。
   */
  day27OnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      let value = control.value;
      if (typeof control.value.toISOString === 'function') {
        value = control.value.toISOString();
      }
      const day = moment(value).format('DD');
      return day === '27' ? null : {day27Only: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 日付型チェック(YYYYMM形式)をバリデートする。
   */
  dateYYYYMMValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      let value = control.value;
      if (typeof control.value.toISOString === 'function') {
        value = control.value.toISOString();
      }
      value = moment(value).format('YYYYMM');
      return this.isDateYYYYMM(value) ? null : {dateYYYYMM: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 日付型チェック(YYYYMMDDhhmmss形式)をバリデートする。
   */
  dateYYYYMMDDhhmmssValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      if (this.getLength(control.value) === 0) {
        return null;
      }
      let value = control.value;
      if (typeof control.value.toISOString === 'function') {
        value = moment(control.value.toISOString()).format('YYYYMMDDhhmmss');
      }
      return this.isDateYYYYMMDDhhmmss(value) ? null : {dateYYYYMMDDhhmmss: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角ひらがなかどうかを判定するバリデータ
   */
  zenkakuHiraganaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuHiragana(control.value) ? null : {zenkakuHiragana: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 全角カタカナかどうかを判定するバリデータ
   */
  zenkakuKatakanaValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isZenkakuKatakana(control.value) ? null : {zenkakuKatakana: true,};
    };
  }

  /**
   * [一般開発者利用可]
   * 日付のFrom～Toチェック
   */
  dateFromToValidator(from: string, to: string, isClearError = true): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const dateFrom = group?.get(from);
      const dateTo = group?.get(to);
      if (isClearError) {
        dateFrom?.setErrors(null);
        dateTo?.setErrors(null);
      }

      // dateFromとdateToの値はYYYYMMDD書式の文字列に変換
      const dateFromValue = moment.isMoment(dateFrom?.value) ?
        (<moment.Moment>dateFrom?.value).format('YYYYMMDD') :
        (this.stringUtil.isEmpty(dateFrom?.value) ?
        SystemConstant.EMPTY : moment(dateFrom?.value).format('YYYYMMDD'));
      const dateToValue = moment.isMoment(dateTo?.value) ?
        (<moment.Moment>dateTo?.value).format('YYYYMMDD') :
        (this.stringUtil.isEmpty(dateTo?.value) ?
        SystemConstant.EMPTY : moment(dateTo?.value).format('YYYYMMDD'));

      if (this.inputCheck.isDateFromToChk(dateFromValue, dateToValue)) {
        if (!isClearError) {
          if (dateFromValue) {
            dateFrom?.setErrors(null);
            dateFrom?.markAsDirty();
          }

          if (dateToValue) {
            dateTo?.setErrors(null);
            dateTo?.markAsDirty();
          }
        }

        return null;
      } else {
        if (control === dateFrom) {
          dateTo?.setErrors({dateFromTo: true,});
          dateTo?.markAsDirty();
        } else {
          dateFrom?.setErrors({dateFromTo: true,});
          dateFrom?.markAsDirty();
        }
        return {dateFromTo: true,};
      }
    };
  }

  /**
   * [一般開発者利用可]
   * 桁数(カンマ削除)をバリデートする。
   */
  maxlengthWithoutCommaValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isMaxlengthWithoutComma(control.value, maxLength) ? null : {maxlengthWithoutComma: true, maxLength: maxLength,};
    }
  }

  /**
   * [一般開発者利用可]
   * 数値かどうかを判定するバリデータ
   * VBのInformation#IsNumericメソッドを移行したメソッドを利用する
   */
  informationIsNumericValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.isInformationIsNumeric(control.value) ? null : {informationIsNumeric: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * NumberのFrom～Toチェック
   */
  numberFromToValidator(from: string, to: string, isClearError = true): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const numberFrom = group?.get(from);
      const numberTo = group?.get(to);
      if (isClearError) {
        numberFrom?.setErrors(null);
        numberTo?.setErrors(null);
      }

      if (
        numberFrom?.value == null ||
        numberFrom?.value === SystemConstant.EMPTY ||
        numberTo?.value == null ||
        numberTo?.value === SystemConstant.EMPTY ||
        Number(numberFrom?.value.replace(/,/g, '')) <= Number(numberTo?.value.replace(/,/g, ''))) {
        return null;
      } else {
        if (control === numberFrom) {
          numberTo?.setErrors({numberFromTo: true,});
          numberTo?.markAsDirty();
        } else {
          numberFrom?.setErrors({numberFromTo: true,});
          numberFrom?.markAsDirty();
        }
        return {numberFromTo: true,};
      }
    };
  }

  /*************************************************************************
   * 以降はユーティリティとして利用可能な汎用バリデーションメソッド(どこからでも利用可) *
   ************************************************************************/

  /**
   * [一般開発者利用可]
   * アルファベットかどうか
   * @param str 検証文字列
   */
  isAlphabet(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfAlphabet);
    return fn(str);
  }

  /**
   * [一般開発者利用可]
   * 全角アルファベットと空白かどうか
   * @param str 検証文字列
   */
   isAlphabetSpace(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfZenAlphabetSpace);
    const isAlphabetSpace = fn(str);
    this.logger.debug('isAlphabetSpace ' + str + ' ' + String(isAlphabetSpace));
    return isAlphabetSpace;
  }

  /**
   * [一般開発者利用可]
   * 半角(数値、英字)かどうか
   * @param str 検証文字列
   */
  isHankaku(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHankaku);
    const isHankaku = fn(str);
    this.logger.debug('isHankaku ' + str + ' ' + String(isHankaku));
    return isHankaku;
  }

  /**
   * [一般開発者利用可]
   * 半角カナ文字
   * @param str 検証文字列
   */
   isHankakuKana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHankakuKana);
    const isHankakuKana = fn(str);
    this.logger.debug('isHankakuKana ' + str + ' ' + String(isHankakuKana));
    return isHankakuKana;
  }

  /**
   * [一般開発者利用可]
   * 全角かどうか
   * @param str 検証文字列
   */
  isZenkaku(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction('^' + this.patternOfZenkaku + '*$');
    const isZenkaku: boolean = fn(str);
    this.logger.debug('isZenkaku ' + str + ' ' + String(isZenkaku));
    if (isZenkaku) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * Shift_JISでの存在チェック
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   existInSjis(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) > 0) {
      const sjisBytes = this.stringUtil.getByteSJIS(str);
      const sjisString = this.stringUtil.getString(sjisBytes, this.stringUtil.C_ENC_SJIS);
      if (this.existInDnp(str) && sjisString === str) {
        this.logger.debug('SHIFT_JIS変換/DNP対応文字チェックO.K.：' + str);
        return true;
      } else {
        this.logger.debug('SHIFT_JIS変換/DNP対応文字チェックN.G.：' + str);
        return false;
      }
    }
    return true;
  }

  /**
   * Shift_JISでの存在チェック(全角のみ)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  existInSjisZenkaku(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) > 0) {
      const sjisBytes = this.stringUtil.getByteSJIS(str);
      const sjisString = this.stringUtil.getString(sjisBytes, this.stringUtil.C_ENC_SJIS);
      const uniCodeArray = Encoding.stringToCode(str);
      if (this.existInDnp2(str) && sjisString === str && sjisBytes.length === uniCodeArray.length * 2) {
        this.logger.debug('SHIFT_JIS変換/DNP対応文字チェックO.K.(全角のみ)：' + str);
        return true;
      } else {
        this.logger.debug('SHIFT_JIS変換/DNP対応文字チェックN.G.(全角のみ)：' + str);
        return false;
      }
    }
    return true;
  }

  /**
   * DNP対応文字チェック
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  existInDnp(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (str.length <= 0) {
      return true;
    }
    const fn = this.matchPatternFunction(this.patternOfExistInDnp)
    this.logger.debug('existInDnp ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * DNP対応文字チェック２
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  existInDnp2(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (str.length <= 0) {
      return true;
    }
    const fn = this.matchPatternFunction(this.patternOfExistInDnp2)
    this.logger.debug('existInDnp2 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * Zip Code
   * @param str1 ZipCode1
   * @param str2 ZipCode2
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZipCode(str1: string | null | undefined, str2: string | null | undefined): boolean {
    if (str1 == null || str2 == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str1) === 3 && this.getLength(str2) === 4) {
      const patternRegex = '^' + this.constantOfSuji + '{7}$';
      const fn = this.matchPatternFunction(patternRegex)
      this.logger.debug('isZipCode ' + str1 + ' ' + str2 + ' ' + fn(str1 + str2));
      return fn(str1 + str2);
    } else {
      this.logger.debug('isZipCode ' + str1 + ' ' + str2 + ' False');
      return false;
    }
  }

  /**
   * Zip Code 1
   * @param str ZipCode
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZipCode1(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + this.constantOfSuji + this.constantOfSuji + '$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isZipCode1 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * Zip Code 2
   * @param str ZipCode
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZipCode2(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{1,4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isZipCode2 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 電話番号
   * @param str1 電話番号NO1
   * @param str2 電話番号NO2
   * @param str3 電話番号NO3
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTelNo(str1: string | null | undefined, str2: string | null | undefined, str3: string | null | undefined): boolean {
    if (str1 == null || str2 == null || str3 == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    const lenghtStr1: number = this.getLength(str1);
    const lenghtStr2: number = this.getLength(str2);
    const lenghtStr3: number = this.getLength(str3);
    const lenghtTotal: number = lenghtStr1 + lenghtStr2 + lenghtStr3;
    if ((2 <= lenghtStr1 && lenghtStr1 <= 5)
      && (0 <= lenghtStr2 && lenghtStr2 <= 4)
      && (4 <= lenghtStr3 && lenghtStr3 <= 6)
      && (9 <= lenghtTotal && lenghtTotal <= 11)) {
      const patternRegex = '^' + this.constantOfSuji + '{9,11}$';
      const fn = this.matchPatternFunction(patternRegex);
      this.logger.debug('isTelNo ' + str1 + ' ' + str2 + ' ' + str3 + ' ' + fn(str1 + str2 + str3));
      return fn(str1 + str2 + str3);
    } else {
      this.logger.debug('isTelNo ' + str1 + ' ' + str2 + ' ' + str3 + ' False' );
      return false;
    }
  }

  /**
   * 電話番号NO1
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTelNo1(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{2,5}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isTelNo1 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 電話番号NO2
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTelNo2(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{1,4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isTelNo2 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 電話番号NO3
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTelNo3(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{4,6}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isTelNo3 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * Keitai電話番号
   * @param str1 Keitai電話番号No1
   * @param str2 Keitai電話番号No2
   * @param str3 Keitai電話番号No3
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKeitaiTelNo(str1: string | null | undefined, str2: string | null | undefined, str3: string | null | undefined): boolean {
    if (str1 == null || str2 == null || str3 == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str1) === 3 && this.getLength(str2) === 4 && this.getLength(str3) === 4) {
      const patternRegex = '^' + this.constantOfSuji + '{11}$';
      const fn = this.matchPatternFunction(patternRegex);
      this.logger.debug('isKeitaiTelNo ' + str1 + ' ' + str2 + ' ' + str3 + ' ' + fn(str1 + str2 + str3));
      return fn(str1 + str2 + str3);
    } else {
      this.logger.debug('isKeitaiTelNo ' + str1 + ' ' + str2 + ' ' + str3 + ' False');
    }
    return false;
  }

  /**
   * Keitai電話番号NO1
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKeitaiTelNo1(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{3}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isKeitaiTelNo1 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * Keitai電話番号NO2
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKeitaiTelNo2(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isKeitaiTelNo2 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * Keitai電話番号NO3
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKeitaiTelNo3(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isKeitaiTelNo3 ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 姓氏 (カナ)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isSeiKana(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfKana + '{1,20}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isSeiKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 姓名 (全角)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isSeiZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithOutBlank + '{1,10}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isSeiZenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 名字 (カナ)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isMeiKana(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfKana + '{1,20}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isMeiKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 名字 (全角)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isMeiZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithOutBlank + '{1,10}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isMeiZenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 会社名(カナ)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isCompanyNameKana(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfKanaWithKakko + '{1,100}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isCompanyNameKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 会社名
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isCompanyNameZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithKigoExtKigo + '{1,50}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isCompanyNameZenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 支店名(カナ)
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isSitenNameKana(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfKanaWithKakko + '{1,100}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isSitenNameKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 支店名
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isSitenNameZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithKigoExtKigo + '{1,50}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isSitenNameZenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 部署名
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isBushoNameZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithKigo + '{1,50}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isBushoNameZenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 都道府県
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isPrefNameZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithOutBlank + '{3,4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isPrefNameZenkaku ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 市名
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isCityNameZenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkaku + '{1,12}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isCityNameZenkaku ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 住所1
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAddName1Zenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkaku + '{1,18}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isAddName1Zenkaku ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 住所2
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAddName2Zenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkaku + '{1,12}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isAddName2Zenkaku ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 住所2
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAddName3Zenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkaku + '{1,10}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isAddName3Zenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 住所末尾２
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAddName4Zenkaku(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithKigoExtKigo + '{1,50}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isAddName4Zenkaku ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 各種書類送付先
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAtena(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithKigoExtKigo + '{1,40}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isAtena ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 通帳記号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTuchoKigo(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{5}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isTuchoKigo ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 通帳No
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTuchoNo(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{8}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isTuchoNo ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 金融機関コード
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKinyuKikanNo(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{4}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isKinyuKikanNo ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 金融機関名チェックは不要であるため、一律Trueを返却する
   * @param str チェック対象文字列
   * @returns TRUE
   */
  // いまはチェックロジック不要でstrが使われないため、lintのdisableを設定
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isKinyuKikanMeiZenkaku(str: string | null | undefined): boolean {
    return true;
  }

  /**
   * 金融機関支店番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKinyuKikanSitenNo(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.constantOfSuji + '{3}$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isKinyuKikanSitenNo ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 支店名チェックは不要であるため、一律Trueを返却する
   * @param str チェック対象文字列
   * @returns TRUE
   */
  // いまはチェックロジック不要でstrが使われないため、lintのdisableを設定
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isKinyuKikanSitenMeiZenkaku(str: string | null | undefined): boolean {
    return true;
  }

  /**
   * 全角文字
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZenkakuWithoutBlank(str: string | null | undefined): boolean {
    const patternRegex = '^' + this.patternOfZenkakuWithOutBlank + '*$';
    const fn = this.matchPatternFunction(patternRegex);
    this.logger.debug('isZenkakuWithoutBlank ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 全角文字と記号と空白
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZenkakuAndKigo(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction( '^' + this.patternOfZenkakuWithKigoExtKigo + '*$');
    this.logger.debug('isZenkakuAndKigo ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return fn(str);
  }

  /**
   * 全角文字と記号（""．""含む）と空白
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZenkakuAndKigoForShouhin(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfZenkakuWithKigoForShouhin);
    this.logger.debug('isZenkakuAndKigoForShouhin ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return fn(str);
  }

  /**
   * 全角文字と記号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isZenkakuAndKigoWithoutBlank(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfZenkakuAndKigoWithOutBlank);
    this.logger.debug('isZenkakuAndKigoWithoutBlank ' + str + ' ' + fn(str));
    if (fn(str)) {
      return this.existInSjis(str);
    }
    return fn(str);
  }

  /**
   * 全角カナ
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfKana);
    this.logger.debug('isKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 全角カナと（）
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isAllKana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfKanaWithKakko);
    this.logger.debug('isAllKana ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * 負の数を認める半角数字
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isMinusNumber(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfMinusNumber);
    this.logger.debug('isMinusNumber ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * メニュー番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isMenuNO(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfMenuNo);
    this.logger.debug('isMenuNO ' + str + ' ' + fn(str));
    return fn(str);
  }

  /**
   * メールアドレス
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isMailAddress(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfMailAddress);
    const isMailAddress = fn(str);
    this.logger.debug('isMailAddress ' + str + ' ' + String(isMailAddress));
    return isMailAddress;
  }

  /**
   * Shounin Number
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isShouninNumber(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) !== 0) {
      this.logger.debug('承認番号：' + str);
      if (this.getLength(str) === 10) {
        isCheck = this.isNumeric(str);
      }
    } else {
      this.logger.debug('承認番号が入力されていないため、承認番号チェックは行いません。');
      isCheck = true;
    }
    this.logger.debug('isShouninNumber ' + str + ' ' + isCheck);
    return isCheck;
  }

  /**
   * Shounin Number(指定桁以上かのチェック)
   * @param str チェック対象文字列
   * @param {number} minLen 最小桁数
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isShouninNumberLen(str: string | null | undefined, minLen: number): boolean {
    let isCheck = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) !== 0) {
      this.logger.debug('承認番号：' + str);
      if (this.getLength(str) >= minLen && this.getLength(str) <= 10) {
        isCheck = this.isNumeric(str);
      }
    } else {
      this.logger.debug('承認番号が入力されていないため、承認番号チェックは行いません。');
      isCheck = true;
    }
    this.logger.debug('isShouninNumberLen ' + str + ' ' + isCheck);
    return isCheck;
  }

  /**
   * KeiyakuUketuke Number
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKeiyakuUketukeNumber(str: string | null | undefined): boolean {
    let isCheck = true;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) !== 0) {
      const fn = this.matchPatternFunction(this.patternOfKeiyakuUketukeNumber);
      isCheck = fn(str);
    } else {
      this.logger.debug('契約受付番号が入力されていないため、契約受付番号チェックは行いません。');
    }
    this.logger.debug('isKeiyakuUketukeNumber ' + str + ' ' + isCheck);
    return isCheck;
  }

  /**
   * 取引番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isTradeNo(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) === 6) {
      isCheck = this.isHankaku(str);
    }
    this.logger.debug('isTradeNo ' + str + ' ' + isCheck)
    return isCheck;
  }

  /**
   * 運転免許証番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isUntenMenkyoNo(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) === 0) {
      isCheck = true;
    } else if (this.getLength(str) === 12) {
      isCheck = this.isNumeric(str);
    }
    this.logger.debug('isUntenMenkyoNo ' + str + ' ' + isCheck);
    return isCheck;
  }

  /**
   * MP契約番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isMPKeiyakuNo(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) === 0) {
      isCheck = true;
    } else if (this.getLength(str) === 12) {
      isCheck = this.isNumeric(str);
    }
    this.logger.debug('isMPKeiyakuNo ' + str + ' ' + isCheck);
    return isCheck;
  }

  /**
   * 文字列に2つ以上連続した空白(全角スペース)が存在する場合、全角スペース1つに変換する。
   * @param str 検知対象文字列
   * @returns 変換後文字列
   */
  fncSpaceCheck(str: string | null | undefined): string {
    this.logger.debug('共通DATACHECK(fncSpaceCheck)：連続空白置換を開始します　値：' + str);
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    while (!this.stringUtil.isEmpty(str)) {
      if (str.indexOf('　　') === -1) {
        break;
      }
      str = str.replace('　　', '　');
    }
    this.logger.debug('共通DATACHECK(fncSpaceCheck)：連続空白置換を終了します　値：' + str);
    return str;
  }

  /**
   * データグリッド表示：文字列に2連続半角ハイフンが存在する場合、消去する。
   * @param str 検知対象文字列
   * @returns 変換後文字列
   */
  fncDGTelCheck(str: string | null | undefined): string {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (str.indexOf(this.constantOfSuji + '--' + this.constantOfSuji) === -1 && str.indexOf('--') === 0) {
      this.logger.debug('共通DATACHECK(fncTelCheck)：電話番号ハイフン置換開始 値：' + str);
      str = str.replace('--', '');
      this.logger.debug('共通DATACHECK(fncTelCheck)：電話番号ハイフン置換終了 値：' + str);
    }
    return str;
  }

  /**
   * 占有面積
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isArea(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (1 <= this.getLength(str) && this.getLength(str) <= 6) {
      const r = new RegExp('^' + this.constantOfSuji + '{1,3}$');
      if (str.indexOf('.') > 0) {
        const arrData: string[] = str.split('.');
        if (arrData.length === 2) {
          const r2 = new RegExp('^' + this.constantOfSuji + '{0,2}$');
          const isValidArea = r.test(arrData[0]) && r2.test(arrData[1]);
          this.logger.debug('IsArea ' + str + ' ' + isValidArea)
          return isValidArea;
        } else {
          this.logger.debug('IsArea ' + str + ' ' + 'False');
          return false;
        }
      } else {
        const isCheck = r.test(str);
        this.logger.debug('IsArea ' + str + ' ' + isCheck);
        return isCheck;
      }
    } else {
      this.logger.debug('IsArea ' + str + ' ' + 'False');
    }
    return false;
  }

  /**
   * 代理店手数料率
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
 isRate(str: string | null | undefined): boolean {
  if (str == null) {
    const messageStack = new MessageStackService(MessageLevel.ERROR);
    messageStack.add({messageId: 'CSS01999M007E',});
    throw messageStack;
  }
  if (1 <= this.getLength(str) && this.getLength(str) <= 6) {
    const r = new RegExp('^' + this.constantOfSuji + '{1,3}$');
    if (str.indexOf('.') > 0) {
      const arrData: string[] = str.split('.');
      if (arrData.length === 2) {
        const r2 = new RegExp('^' + this.constantOfSuji + '{0,2}$');
        const isValidRate = r.test(arrData[0]) && r2.test(arrData[1]);
        this.logger.debug('IsRate ' + str + ' ' + isValidRate)
        return isValidRate;
      } else {
        this.logger.debug('IsRate ' + str + ' ' + 'False');
        return false;
      }
    } else {
      const isCheck = r.test(str);
      this.logger.debug('IsRate ' + str + ' ' + isCheck);
      return isCheck;
    }
  } else {
    this.logger.debug('IsRate ' + str + ' ' + 'False');
  }
  return false;
}

  /**
   * 全角カナと空白
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKanaSpace(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      this.logger.debug('引数の有効性チェック エラー');
      return false;
    }
    this.logger.debug('IsKanaSpaceチェック開始');
    this.logger.debug('引数:' + str);
    const fn = this.matchPatternFunction(this.patternOfKanaSpace);
    isCheck = fn(str);
    if (isCheck) {
      this.logger.debug('IsKanaSpaceチェック:OK');
    } else {
      this.logger.debug('IsKanaSpaceチェック:NG');
    }
    this.logger.debug('IsKanaSpaceチェック終了');
    return isCheck;
  }

  /**
   * 全角空白が１文字のみのチェック
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isSpace(str: string | null | undefined): boolean {
    let isCheck = false;
    if (str == null) {
      this.logger.debug('引数の有効性チェック エラー');
      return false;
    }
    this.logger.debug('IsSpaceチェック開始');
    this.logger.debug('引数:' + str);
    const arrData: string[] = str.split('　');
    if (arrData.length === 2) {
      this.logger.debug('IsSpaceチェック:OK');
      isCheck = true;
    } else {
      this.logger.debug('IsSpaceチェック:NG');
      isCheck = false;
    }
    this.logger.debug('IsSpaceチェック終了');
    return isCheck;
  }

  /**
   * データグリッド表示：文字列に連結していない空白(全角スペース)が存在する場合、消去する。
   * @param str 検知対象文字列
   * @returns 変換後文字列
   */
  fncDGSpaceCheck(str: string | null | undefined): string {
    const r = new RegExp('^' + '　');
    const l = new RegExp('　' + '$');
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    let changChar: string = str;
    if (l.test(changChar) || r.test(changChar) || changChar.indexOf('　　') > -1) {
      this.logger.debug('共通DATACHECK(fncDGSpaceCheck)：開始前 値：' + changChar);
      if (changChar.indexOf('　　') > -1) {
        changChar = this.fncSpaceCheck(changChar);
      }
      if (l.test(changChar) || r.test(changChar)) {
        this.logger.debug('共通DATACHECK(fncDGSpaceCheck)：前後の空白を削除');
        changChar = changChar.trim();
      }
      this.logger.debug('共通DATACHECK(fncDGSpaceCheck)：終了 値：' + changChar);
    }
    return changChar;
  }

  /**
   * 文字列作成「以上、××支店分」
   * @param strPASitenName
   * @param intAllSILen
   * @returns
   */
  fncSiNamePadding(strPASitenName: string | null | undefined, intAllSILen: number | null | undefined): string {
    //①元画面から取得した支店名の文字数
    let intPASILen = 0;
    //③「以上、」と「分」を足した文字数
    let intSonotaLen = 0;
    //④「＊」の基本個数
    let intAsterLen = 0;
    //④ベースとなる数値(②-③-④)
    let intBaseLen = 0;
    //②-③-①を引いて、2で割ったものの商
    let intCalSYOU = 0;
    //②-③-①を引いて、2で割ったものの余
    let intCalYOJO = 0;

    //ループ用かカウンタ
    let pACounter = 0;

    //戻り値「支店名」
    let rtnPASitenName = '';

    //初期値入力
    intSonotaLen = 4;
    intAsterLen = 4;
    if(intAllSILen == null){
      intAllSILen = 0;
    }
    intBaseLen = intAllSILen - intSonotaLen - intAsterLen;

    if (strPASitenName == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    this.logger.debug('◆文字列作成「以上、××支店分」開始');
    this.logger.debug('引数「支店名」: ' + strPASitenName);

    intPASILen = strPASitenName === null ? 0 : this.getLength(strPASitenName);
    // ＊(前後で1個ずつ)使用するので2で割る
    intCalSYOU = Math.floor((intAllSILen - intSonotaLen - intPASILen) / 2);
    // 上記の余り（奇数になった時に空白を足す処理をする為）
    intCalYOJO = (intAllSILen - intSonotaLen - intPASILen) % 2;
    if (intPASILen < intBaseLen) {
      pACounter = 0;
      while (intCalSYOU !== pACounter) {
        rtnPASitenName += '＊';
        pACounter = pACounter + 1;
      }
      rtnPASitenName += '以上、';
      rtnPASitenName += strPASitenName;
      rtnPASitenName += '分';

      pACounter = 0;
      while (intCalSYOU !== pACounter) {
        rtnPASitenName += '＊';
        pACounter = pACounter + 1;
      }

      if (intCalYOJO !== 0) {
        rtnPASitenName += '　';
      }

    } else if (intPASILen === intBaseLen) {
      rtnPASitenName += '＊＊';
      rtnPASitenName += '以上、';
      rtnPASitenName += strPASitenName;
      rtnPASitenName += '分';
      rtnPASitenName += '＊＊';
    } else if (intPASILen > intBaseLen) {
      //↓intBaseLenにIntegerに変換して桁落ちするほどの値が入ることは無く
      //↓現状で問題ないので修正しない
      strPASitenName = strPASitenName.substring(0, intBaseLen);
      rtnPASitenName += '＊＊';
      rtnPASitenName += '以上、';
      rtnPASitenName += strPASitenName;
      rtnPASitenName += '分';
      rtnPASitenName += '＊＊';
    }
    this.logger.debug('戻り値「支店名」:' + rtnPASitenName);
    this.logger.debug('◆文字列作成「以上、××支店分」終了');
    return rtnPASitenName;
  }

  /**
   * 日付範囲チェック
   * @param dateCheck  YYYY/MM/DD形式の日付(String型)
   * @returns
   */
  fncDateRangeCheck(dateCheck: string | null | undefined): boolean {
    const startDate = moment('1753/01/01', 'YYYY/MM/DD');
    const endDate = moment('9998/12/31', 'YYYY/MM/DD');
    const checkDate = moment(dateCheck, 'YYYY/MM/DD');
    const isValid = checkDate.isBetween(startDate, endDate, void 0, '[]');

    this.logger.debug('DateRangeCheck開始');

    if (isValid) {
      this.logger.debug('日付範囲チェック：OK');
    } else {
      this.logger.debug('日付範囲チェック：NG');
    }
    return isValid;
  }

  /**
   * 概算率チェック
   * @param str 検知対象文字列
   * @returns
   */
  isGaisanritsu(str: string): boolean {
    if (this.stringUtil.isEmpty(str)) {
      return true;
    }
    if (this.isNumeric(str)) {
      if (0 <= parseInt(str) && parseInt(str) <= 100) {
        this.logger.debug('isGaisanritsu ' + str + ' ' + 'true');
        return true;
      }
    }
    return false;
  }

  /**
   * 日付型チェック(YYYYMMDD形式)
   * @param str YYYYMMDD形式の日付(String型)
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isDateYYYYMMDD(str: string): boolean {
    let isRtn = false;
    let sb;
    // 引数の有効性チェック
    if (str == null) {
      this.logger.debug('引数の有効性チェック エラー');
      return isRtn;
    }

    this.logger.debug('IsDate_YYYYMMDDチェック開始');
    this.logger.debug('引数:' + str);

    // 文字数チェック(8桁であること)
    if (this.getLength(str) === 8) {
      this.logger.debug('文字数チェック：OK');
      isRtn = true;
    } else {
      this.logger.debug('文字数チェック：NG');
      isRtn = false;
    }

    // 半角数値型であること
    if (isRtn) {
      if (this.isNumeric(str)) {
        this.logger.debug('数値チェック：OK');
        isRtn = true;
      } else {
        this.logger.debug('数値チェック：NG');
        isRtn = false;
      }
    }
    // スラッシュ変換
    const wkVal = this.dateUtil.convertDateString(
      str,
      'YYYYMMDD',
      'YYYY/MM/DD'
    );

    // 日付に変換できること
    if (isRtn) {
      sb = SystemConstant.EMPTY;
      sb += '日付型変換チェック引数：';
      sb += wkVal;
      this.logger.debug(sb);
      const isValidDate = moment(str, 'YYYY/MM/DD').isValid();
      if (isValidDate) {
        this.logger.debug('日付型変換チェック：OK');
        isRtn = true;
      } else {
        this.logger.debug('日付型変換チェック：NG');
        isRtn = false;
      }
    }

    // DateTimePickerで表示できる範囲であること
    if (isRtn) {
      sb = SystemConstant.EMPTY;
      sb += 'fncDateRangeCheckチェック引数：';
      sb += wkVal;
      this.logger.debug(sb);

      if (this.fncDateRangeCheck(str)) {
        isRtn = true;
      } else {
        isRtn = false;
      }
    }

    // 全てチェックがOKだった場合
    if (isRtn) {
      this.logger.debug('IsDate_YYYYMMDDチェックOK');
    }
    return isRtn;
  }

  /**
   * 日付型チェック(YYYYMM形式)
   * @param str YYYYMM形式の日付(String型)
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isDateYYYYMM(str: string): boolean {
    //引数の有効性チェック
    if (str == null) {
      this.logger.debug('引数の有効性チェック エラー');
      return false;
    }
    this.logger.debug('IsDate_YYYYMMチェック開始');
    this.logger.debug('引数:' + str);
    // YYYYMM形式からYYYYMM01形式に変換
    str += '01';
    // 日付型チェック呼び出し
    const isValidDate = this.isDateYYYYMMDD(str);
    // 全てチェックがOKだった場合
    if (isValidDate) {
      this.logger.debug('IsDate_YYYYMMチェックOK');
    }
    return isValidDate;
  }

  /**
   * 日付型チェック(YYYYMMDDhhmmss形式)
   * @param str YYYYMMDDhhmmss形式の日付(String型)
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isDateYYYYMMDDhhmmss(str: string): boolean {
    let isValidDate = false;
    // 引数の有効性チェック
    if (typeof str === 'undefined') {
      this.logger.debug('引数の有効性チェック エラー');
      isValidDate = false;
    }
    this.logger.debug('IsDate_YYYYMMDDhhmmssチェック開始');
    this.logger.debug('引数:' + str);
    const isValidFormat = moment(str, 'YYYYMMDDhhmmss').isValid();
    if (!isValidFormat) {
      return false;
    }
    str = moment(str).format('YYYYMMDDhhmmss');
    if (this.getLength(str) === 14) {
      this.logger.debug('文字数チェック：OK');
      if(this.isNumeric(str)) {
        this.logger.debug('数値チェック：OK');
        const strDate = str.substring(0, 8);
        if (this.isDateYYYYMMDD(strDate)) {
          const isValidDateTime = moment(str, 'YYYY/MM/DD hh:mm:ss').isValid();
            if (isValidDateTime) {
              this.logger.debug('日付型変換チェック：OK');
              this.logger.debug('IsDate_YYYYMMDDhhmmssチェックOK');
              isValidDate = true;
            } else {
              this.logger.debug('日付型変換チェック：NG');
              isValidDate = false;
            }
        }
      } else {
        this.logger.debug('数値チェック：NG');
      }
    } else {
      this.logger.debug('文字数チェック：NG');
    }
    this.logger.debug('IsDate_YYYYMMDDhhmmssチェック終了');
    return isValidDate;
  }

  /**
   * [一般開発者利用可]
   * アスキー文字かどうか
   * @param str 検証文字列
   */
  isAscii(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfAscii);
    return fn(str);
  }

  /**
   * [一般開発者利用可]
   * 数字かどうか
   * @param str 検証文字列
   */
  isNumeric(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfNumeric);
    const isNumeric = fn(str);
    this.logger.debug('IsOnlyNumber ' + str + ' ' + String(isNumeric));
    return isNumeric;
  }

  /**
   * [一般開発者利用可]
   * 半角カタカナかどうか
   * @param str 検証文字列
   */
  isHankakuKatakana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHankakuKatakana);
    const isHankakuKatakana = fn(str);
    this.logger.debug('isHankakuKatakana ' + str + ' ' + String(isHankakuKatakana));
    return isHankakuKatakana;
  }

  /**
   * [一般開発者利用可]
   * 全角ひらがなかどうか
   * @param str 検証文字列
   */
  isZenkakuHiragana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfZenkakuHiragana);
    const isZenkakuHiragana = fn(str);
    this.logger.debug('isZenkakuHiragana ' + str + ' ' + String(isZenkakuHiragana));
    return isZenkakuHiragana;
  }

  /**
   * [一般開発者利用可]
   * 全角カタカナかどうか
   * @param str 検証文字列
   */
  isZenkakuKatakana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfZenkakuKatakana);
    const isZenkakuKatakana = fn(str);
    this.logger.debug('isZenkakuKatakana ' + str + ' ' + String(isZenkakuKatakana));
    return isZenkakuKatakana;
  }

  /**
   * Koza Name Kana
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKozaNameKana(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (1 <= this.getLength(str) && this.getLength(str) <= 50) {
      const pattern = '^' + this.consKozaMeigiKana + '+　?' + this.consKozaMeigiKana + '{0,49}$';
      const fn = this.matchPatternFunction(pattern);
      const isKozaNameKana = fn(str);
      this.logger.debug('isKozaNameKana ' + str + ' ' + String(isKozaNameKana));
      return isKozaNameKana;
    }
    this.logger.debug('isKozaNameKana ' + str + ' False');
    return false;
  }

  /**
   * 口座番号
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKozaNo(str: string | null | undefined): boolean {
    const pattern = '^' + this.constantOfSuji + '{7}$';
    const fn = this.matchPatternFunction(pattern);
    const isKozaNo = fn(str);
    this.logger.debug('isKozaNo ' + str + ' ' + String(isKozaNo));
    return isKozaNo;
  }

  /**
   * 口座名義（カナ）（引当用振込人名カナ用）
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKozaNameKanaNotSpace(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHikiateKozaMeigiKana);
    const isKozaNameKanaNotSpace = fn(str);
    this.logger.debug('isKozaNameKanaNotSpace ' + str + ' ' + String(isKozaNameKanaNotSpace));
    return isKozaNameKanaNotSpace;
  }

  /**
   * 口座名義
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKozaNameZenkaku(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction('^' + this.patternOfKozaMeigi + '{1,50}$');
    const isKozaNameZenkaku = fn(str);
    this.logger.debug('isKozaNameZenkaku ' + str + ' ' + String(isKozaNameZenkaku));
    if (isKozaNameZenkaku) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * IsBldg NameKana
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isBldgNameKana(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction('^' + this.constantOfKana + '{1,100}$');
    const isBldgNameKana = fn(str);
    this.logger.debug('isBldgNameKana ' + str + ' ' + String(isBldgNameKana));
    return isBldgNameKana;
  }

  /**
   * 物件名
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isBldgNameZenkaku(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction('^' + this.patternOfZenkakuWithKigoExtKigo + '{1,50}$');
    const isBldgNameZenkaku = fn(str);
    this.logger.debug('isBldgNameZenkaku ' + str + ' ' + String(isBldgNameZenkaku));
    if (isBldgNameZenkaku) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * 部屋番号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isRoomNoZenkaku(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction('^' + this.patternOfZenkakuWithKigoExtKigo + '{1,10}$');
    const isRoomNoZenkaku = fn(str);
    this.logger.debug('isRoomNoZenkaku ' + str + ' ' + String(isRoomNoZenkaku));
    if (isRoomNoZenkaku) {
      return this.existInSjis(str);
    }
    return false;
  }

  /**
   * ユーザーIDチェック(英字(大文字)、数字、「-(ハイフン)」のみ許可)
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isUserId(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfUserId);
    const isUserId = fn(str);
    this.logger.debug('isUserId ' + str + ' ' + String(isUserId));
    return isUserId;
  }

  /**
   * パスワードチェック(「"(ダブルクォーテーション)」を除く)
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isPassWord(str: string | null | undefined): boolean {
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    const isPassWord: boolean = str.indexOf(this.patternOfPasswordIgnore) < 0
    this.logger.debug('isPassWord ' + String(isPassWord));
    return isPassWord
  }

  /**
   * KISSコード1
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKissCode1(str: string | null | undefined): boolean {
    let isValid = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) === 6) {
      isValid = this.isNumeric(str);
    }
    this.logger.debug('isKissCode1 ' + str + ' ' + isValid);
    return isValid;
  }

  /**
   * KISSコード2
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isKissCode2(str: string | null | undefined): boolean {
    let isValid = false;
    if (str == null) {
      const messageStack = new MessageStackService(MessageLevel.ERROR);
      messageStack.add({messageId: 'CSS01999M007E',});
      throw messageStack;
    }
    if (this.getLength(str) === 3) {
      isValid = this.isNumeric(str);
    }
    this.logger.debug('isKissCode2 ' + str + ' ' + isValid);
    return isValid;
  }

  /**
   * 半角数字と小数点
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isNumberAndPeriod(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfSujiWithPeriod);
    const isNumberAndPeriod = fn(str);
    this.logger.debug('isNumberAndPeriod ' + str + ' ' + String(isNumberAndPeriod));
    return isNumberAndPeriod;
  }

  /**
   * 半角文字と小数点(桁数チェック付)
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isNumberAndPeriodWithSize(str: string | null | undefined, insize: number | null | undefined, decsize: number | null | undefined): boolean {
    let isValid = false;
    if(insize == null){
      insize = 0;
    }
    if(decsize == null){
      decsize = 0;
    }
    if (this.isNumberAndPeriod(str)) {
      // 正規表現チェック
      const newRegex = '^(0|' + this.constantOfSuji + '{1,' + insize + '})(\\.' + this.constantOfSuji + '{1,' + decsize + '})?$';
      if(insize < 1 || decsize < 1){
        const messageStack = new MessageStackService(MessageLevel.ERROR);
        messageStack.add({messageId: 'CSS02019M001E',messageParams: [newRegex,],});
        throw messageStack;
      }
      const fn = this.matchPatternFunction(newRegex);
      isValid = fn(str);
    } else {
      isValid = false;
    }
    this.logger.debug('isNumberAndPeriod(' + insize + ',' + decsize + ') ' + str + ' ' + isValid);
    return isValid;
  }

  /**
   * 半角数字(カンマ削除)
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isOnlyNumberComma(str: string): boolean {
    let isValid = false;
    this.logger.debug('■半角数字(カンマ削除)処理を開始');
    if (this.stringUtil.isEmpty(str)) {
      isValid = true;
    } else {
      // カンマを削除
      const chkstr = str.replace(/,/g, '').trim();
      if (chkstr.length > 0 && this.isNumeric(chkstr)) {
        isValid = true;
      }
    }
    this.logger.debug('■半角数字(カンマ削除)処理を終了');
    return isValid;
  }

  /**
   * 半角文字と記号
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
  isHankakuAndKigo(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHankakuWithKigo);
    const isHankakuAndKigo = fn(str);
    this.logger.debug('isHankakuAndKigo ' + str + ' ' + String(isHankakuAndKigo));
    return isHankakuAndKigo;
  }

  /**
   * 半角数字,半角カナ,半角アルファベット,半角スペース,半角ハイフン
   * @param str チェック対象文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isHankakuWithoutKigo(str: string | null | undefined): boolean {
    const fn = this.matchPatternFunction(this.patternOfHankakuWithOutKigo);
    const isHankakuWithoutKigo = fn(str);
    this.logger.debug('isHankakuWithoutKigo ' + str + ' ' + String(isHankakuWithoutKigo));
    return isHankakuWithoutKigo;
  }

  /**
   * [一般開発者利用可]
   * 文字列長チェック
   * @param str 検証文字列
   * @param maxLength 最大長
   */
  checkLength(str: string, maxLength: number): boolean {
    // 文字数でチェック。桁数をバイト数でチェックしたい場合は、getByteLengthを呼び出す
    return this.getLength(str) <= maxLength;
  }


  /**
   * [一般開発者利用可]
   * パターン検証関数取得
   * @param pattern パターン文字列
   * @returns 検証関数（文字列→結果）
   */
  matchPatternFunction(pattern: string): (str: string | null | undefined) => boolean {
    const fn = (str: string | null | undefined) => {
      if (str == null) {
        const messageStack = new MessageStackService(MessageLevel.ERROR);
        messageStack.add({messageId: 'CSS01999M007E',});
        throw messageStack;
      }
      if (str === '') {
        return true;
      }
      const regex = new RegExp(pattern);
      return regex.test(str);
    };
    return fn;
  }

  /**
   * [一般開発者利用可]
   * 文字数取得
   * @param str 文字列
   */
  getLength(str: string): number {
    if (this.stringUtil.isEmpty(str)) {
      return 0;
    }
    return str.length;
  }

  /**
   * [一般開発者利用可]
   * バイト数取得（半角は1文字、それ以外は2文字）
   * @param str 文字列
   */
  getByteLength(str: string  | null | undefined): number {
    if (str == null || str === '') {
      return 0;
    }
    let length = 0;
    for (let i = 0; i < str.length; i++) {
      const c = str.charCodeAt(i);
      if ((c >= 0x0 && c < 0x81) || (c === 0xf8f0) || (c >= 0xff61 && c < 0xffa0) || (c >= 0xf8f1 && c < 0xf8f4)) {
        length += 1;
      } else {
        length += 2;
      }
    }
    return length;
  }

  /**
   * [一般開発者利用可]
   * オブジェクトの色変更
   * @param formGroup フォームコントロールグループ
   * @param formControlName フォームのコントロール名
   * @return チェック結果(True:チェックOK、False:チェックNG)
   */
  objColorChange(formGroup: FormGroup, formControlName: string): void {
    this.logger.debug('■オブジェクトの色変更処理開始');

    // テキストボックスの場合とラベルの場合
    formGroup.controls[formControlName].setErrors({'incorrect': true,});

    this.logger.debug('■オブジェクトの色変更処理終了');
  }

  /**
   * [一般開発者利用可]
   * エラー制御をクリアする。
   * @param formGroup フォームコントロールグループ
   * @param formControlName フォームのコントロール名
   * @return エラー制御をクリアする
   */
  clearErrorValidation(formGroup: FormGroup, formControlName: string): void {
    // テキストボックスの場合とラベルの場合
    formGroup.controls[formControlName].setErrors(null);
  }

  /**
   * [一般開発者利用可]
   * 文字数チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  lengthCheckValidator(maxLen: number | null | undefined, minLen: number | null | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.lengthCheck(control.value, maxLen, minLen) ? null : {lengthCheck: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角数字チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  onlyNumberLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isOnlyNumberLen(control.value, maxLen, minLen) ? null : {onlyNumberLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 負の数を認める半角数字チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  minusNumberLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isMinusNumberLen(control.value, maxLen, minLen) ? null : {minusNumberLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角数字と小数点チェック(色づけ)
   *
   * @param {number} intLen 整数部の文字数
   * @param {number} decLen 小数部の文字数
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  numberAndPeriodLenValidator(intLen: number | null | undefined, decLen: number | null | undefined,
      maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isNumberAndPeriodLen(control.value, intLen, decLen, maxLen, minLen) ? null : {numberAndPeriodLen: true,};
    }
  }

   /**
   * [一般開発者利用可]
   * 半角文字チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  hankakuLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isHankakuLen(control.value, maxLen, minLen) ? null : {hankakuLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  zenkakuWithoutBlankLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isZenkakuWithoutBlankLen(control.value, maxLen, minLen) ? null : {zenkakuWithoutBlankLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と空白チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  zenkakuLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const checkValVO = new CheckValByrefArgVO();
      checkValVO.chkVal = control.value;
      return this.inputCheck.isZenkakuLen(checkValVO, maxLen, minLen) ? null : {zenkakuLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と記号と空白チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  zenkakuAndKigoLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const checkValVO = new CheckValByrefArgVO();
      checkValVO.chkVal = control.value;
      return this.inputCheck.isZenkakuAndKigoLen(checkValVO, maxLen, minLen) ? null : {zenkakuAndKigoLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角文字と記号（""．""含む）と空白チェック
   *
   * @param maxLen 最大Byte数
   * @param minLen 最小Byte数
   */
  zenkakuAndKigoForShouhinLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isZenkakuAndKigoForShouhinLen(control.value, maxLen, minLen) ? null : {zenkakuAndKigoForShouhinLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角カナチェック
   *
   * @param maxLen 最大Byte数
   * @param minLen 最小Byte数
   */
  hankakuKanaLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isHankakuKanaLen(control.value, maxLen, minLen) ? null : {hankakuKanaLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナチェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  kanaLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isKanaLen(control.value, maxLen, minLen) ? null : {kanaLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナと（）チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  allKanaLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isAllKanaLen(control.value, maxLen, minLen) ? null : {allKanaLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角カナと空白チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  kanaSpaceLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isKanaSpaceLen(control.value, maxLen, minLen) ? null : {kanaSpaceLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 全角アルファベットと空白チェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  alphabetSpaceLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isAlphabetSpaceLen(control.value, maxLen, minLen) ? null : {alphabetSpaceLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * SHIFT-JISチェック
   *
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  extInSjLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isExistInSjisLen(control.value, maxLen, minLen) ? null : {extInSjLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 半角数字,半角カナ,半角アルファベット,半角スペース,半角ハイフンチェック(色づけ)
   * @param {number} maxLen 最大Byte数
   * @param {number} minLen 最小Byte数
   */
  hankakuWithoutKigoLenValidator(maxLen: number | null | undefined = 0, minLen: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isHankakuWithoutKigoLen(control.value, maxLen, minLen) ? null : {hankakuWithoutKigoLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 会社名チェック(色づけ)
   */
  companyNameZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isCompanyNameZenkakuLen(control.value) ? null : {companyNameZenkakuLen: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 郵便番号チェック(色づけ)
   *
   * @param {string} zipCode1 郵便番号1オブジェクト
   * @param {string} zipCode2 郵便番号2オブジェクト
   *
   */

  zipCodeLenValidator(zipCode1: string, zipCode2: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const dataControl1 = group?.get(zipCode1);
      const dataControl2 = group?.get(zipCode2);
      dataControl1?.setErrors(null);
      dataControl2?.setErrors(null);
      if (this.inputCheck.isZipCodeLen(dataControl1?.value, dataControl2?.value)) {
        return null;
      } else {
        dataControl1?.setErrors({zipCodeLen: true,});
        dataControl1?.markAsDirty();
        dataControl2?.setErrors({zipCodeLen: true,});
        dataControl2?.markAsDirty();
        return {zipCodeLen: true,};
      }
    }
  }

  /**
   * [一般開発者利用可]
   * 電話番号チェック(色づけ)
   *
   * @param {string} telNo1 電話番号1オブジェクト
   * @param {string} telNo2 電話番号2オブジェクト
   * @param {string} telNo3 電話番号3オブジェクト
   */
  telNoLenValidator(telNo1: string, telNo2: string, telNo3: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const ctrTelNo1 = group?.get(telNo1);
      const ctrTelNo2 = group?.get(telNo2);
      const ctrTelNo3 = group?.get(telNo3);
      ctrTelNo1?.setErrors(null);
      ctrTelNo2?.setErrors(null);
      ctrTelNo3?.setErrors(null);
      if (this.inputCheck.isTelNoLen(ctrTelNo1?.value, ctrTelNo2?.value, ctrTelNo3?.value)) {
        return null;
      } else {
        ctrTelNo1?.setErrors({telNoLen: true,});
        ctrTelNo1?.markAsDirty();

        ctrTelNo2?.setErrors({telNoLen: true,});
        ctrTelNo2?.markAsDirty();

        ctrTelNo3?.setErrors({telNoLen: true,});
        ctrTelNo3?.markAsDirty();
        return {telNoLen: true,};
      }
    }
  }

  /**
   * [一般開発者利用可]
   * 携帯電話番号チェック(色づけ)
   *
   * @param {string} keitaiTelNo1 携帯電話番号1オブジェクト
   * @param {string} keitaiTelNo2 携帯電話番号2オブジェクト
   * @param {string} keitaiTelNo3 携帯電話番号3オブジェクト
   */
  keitaiTelNoLenValidator(keitaiTelNo1: string, keitaiTelNo2: string, keitaiTelNo3: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const ctrKeitaiTelNo1 = group?.get(keitaiTelNo1);
      const ctrKeitaiTelNo2 = group?.get(keitaiTelNo2);
      const ctrKeitaiTelNo3 = group?.get(keitaiTelNo3);
      ctrKeitaiTelNo1?.setErrors(null);
      ctrKeitaiTelNo2?.setErrors(null);
      ctrKeitaiTelNo3?.setErrors(null);
      if (this.inputCheck.isKeitaiTelNoLen(ctrKeitaiTelNo1?.value, ctrKeitaiTelNo2?.value, ctrKeitaiTelNo3?.value)) {
        return null;
      } else {
        ctrKeitaiTelNo1?.setErrors({keitaiTelNoLen: true,});
        ctrKeitaiTelNo1?.markAsDirty();

        ctrKeitaiTelNo2?.setErrors({keitaiTelNoLen: true,});
        ctrKeitaiTelNo2?.markAsDirty();

        ctrKeitaiTelNo3?.setErrors({keitaiTelNoLen: true,});
        ctrKeitaiTelNo3?.markAsDirty();
        return {keitaiTelNoLen: true,};
      }
    }
  }

  /**
   * [一般開発者利用可]
   * 口座名義（カナ）チェック(色づけ)
   *
   * @param {number} maxLength 最大Byte数
   * @param {number} minLength 最小Byte数
   */
  kozaNameKanaLenValidator(maxLength: number | null | undefined = 0, minLength: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isKozaNameKanaLen(control.value, maxLength, minLength) ? null : {kozaNameKanaLen: true, minLength: minLength, maxLength: maxLength,};
    }
  }

  /**
   * [一般開発者利用可]
   * 口座名義（カナ）（引当用振込人名カナ用）
   *
   * @param {number} maxLength 最大Byte数
   * @param {number} minLength 最小Byte数
   */
  kozaNameKanaNotSpaceLenValidator(maxLength: number | null | undefined = 0, minLength: number | null | undefined = 0): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isKozaNameKanaNotSpaceLen(control.value, maxLength, minLength) ? null : {kozaNameKanaNotSpaceLen: true,};
    }
  }

  /**
   * [一般開発者利用可]
   * 会社名カナチェック(色づけ)
   */
  companyNameKanaLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isCompanyNameKanaLen(control.value) ? null : {companyNameKanaLen: true, minLength: 1, maxLength: 100,};
    }
  }

  /**
   * [一般開発者利用可]
   * 物件名チェック(色づけ)
   */
  bldgNameZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isBldgNameZenkakuLen(control.value) ? null : {bldgNameZenkakuLen: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 部屋番号チェック(色づけ)
   */
  roomNameZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isRoomNameZenkakuLen(control.value) ? null : {roomNameZenkakuLen: true, minLength: 1, maxLength: 10,};
    }
  }

  /**
   * [一般開発者利用可]
   * 部屋番号チェック(色づけ)
   */
  roomNoZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isRoomNoZenkakuLen(control.value) ? null : {roomNoZenkakuLen: true, minLength: 1, maxLength: 10,};
    }
  }

  /**
   * [一般開発者利用可]
   * 番地号チェック(色づけ)
   */
  addName3ZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isAddName3ZenkakuLen(control.value) ? null : {addName3ZenkakuLen: true, minLength: 1, maxLength: 10,};
    }
  }

  /**
   * [一般開発者利用可]
   * 建物名などチェック(色づけ)
   */
  addName4ZenkakuLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isAddName4ZenkakuLen(control.value) ? null : {addName4ZenkakuLen: true, minLength: 1, maxLength: 50,};
    }
  }

  /**
   * [一般開発者利用可]
   * 運転免許番号チェック(色づけ)
   */
  untenMenkyoNoLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isUntenMenkyoNoLen(control.value) ? null : {untenMenkyoNoLen: true, requiredLength: 12,};
    }
  }

  /**
   * [一般開発者利用可]
   * MP契約番号チェック(色づけ)
   */
  mPKeiyakuNoLenValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      return this.inputCheck.isMPKeiyakuNoLen(control.value) ? null : {mPKeiyakuNoLen: true, requiredLength: 12,};
    }
  }

  /**
   * [一般開発者利用可]
   * 日付型チェック
   * @param {string} yy 年
   * @param {string} mm 月
   * @param {string} dd 日
   * @returns {ValidatorFn}
   */
   dateValidator(yy: string, mm: string, dd: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.pristine) {
        return null;
      }
      const group = control.parent;
      const yyPart = group?.get(yy);
      const mmPart = group?.get(mm);
      const ddPart = group?.get(dd);
      let sb = '';
      sb = sb.concat('20');
      sb = sb + yyPart?.value.padStart(2, '0');
      sb = sb + mmPart?.value.padStart(2, '0');
      sb = sb + ddPart?.value.padStart(2, '0');

      yyPart?.setErrors(null);
      mmPart?.setErrors(null);
      ddPart?.setErrors(null);

      if (
        this.isDateYYYYMMDD(sb) &&
        this.inputCheck.isOnlyNumberLen(yyPart?.value) &&
        this.inputCheck.isOnlyNumberLen(mmPart?.value) &&
        this.inputCheck.isOnlyNumberLen(ddPart?.value)) {
        // 何もしない
        return null;
      } else {
        yyPart?.setErrors({datePart: true,});
        yyPart?.markAsDirty();
        mmPart?.setErrors({datePart: true,});
        mmPart?.markAsDirty();
        ddPart?.setErrors({datePart: true,});
        ddPart?.markAsDirty();

        return {datePart: true,};
      }
    }
  }

  /**
   * 桁数チェック(カンマ削除)
   * @param str 検証文字列
   * @param maxLength 最大桁数
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isMaxlengthWithoutComma(str: string, maxLength: number): boolean {
    let isValid = false;
    this.logger.debug('■桁数チェック(カンマ削除)処理を開始');
    if (this.stringUtil.isEmpty(str)) {
      isValid = true;
    } else {
      // カンマを削除
      const chkstr = str.replace(/,/g, '').trim();
      if (chkstr.length <= maxLength) {
        isValid = true;
      }
    }
    this.logger.debug('■桁数チェック(カンマ削除)処理を終了');
    return isValid;
  }

  /**
   * 数値かどうかを判定する
   * @param str 検証文字列
   * @returns チェック結果(True:チェックOK、False:チェックNG)
   */
   isInformationIsNumeric(str: string): boolean {
    let isValid = false;
    this.logger.debug('■isInformationIsNumeric処理を開始');
    if (this.stringUtil.isEmpty(str)) {
      isValid = true;
    } else {
      // VBのInformation#IsNumericメソッドを移行したメソッドを呼び出す
      isValid = this.stringUtil.isNumeric(str);
    }
    this.logger.debug('■isInformationIsNumeric処理を終了');
    return isValid;
  }

  /**
   * Reactive Forms 機能のカスタムヴァリデーションルール。
   * 条件付きルールがトリガーされたときにヴァリデーションを実行する。
   * @param predicate 条件をチェックする関数
   * @param validator フォーム コントロールのバリデーター
   * @returns
   */
  // eslint-disable-next-line
  conditionalValidator(predicate: Function, validator: any): any {
    return ((control: AbstractControl) => {
      if (!control.parent) {
        return null;
      }
      if (predicate()) {
        return validator(control);
      }
      return null;
    });
  }
}
